import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { UserStatsState, UserStatsActions } from '../types';
import { getApiService } from '../api/services';

interface UserStatsStore extends UserStatsState {
  actions: UserStatsActions;
}

export const useUserStatsStore = create<UserStatsStore>()(
  devtools(
    set => ({
      // Initial state
      stats: null,
      interactions: [],
      progress: null,
      isLoading: false,
      error: null,

      // Actions
      actions: {
        loadStats: async () => {
          try {
            set(
              { isLoading: true, error: null },
              false,
              'userStats/loadStats/start'
            );

            const apiService = getApiService();
            const stats = await apiService.getStats();

            set(
              {
                stats,
                isLoading: false,
                error: null,
              },
              false,
              'userStats/loadStats/success'
            );
          } catch (error) {
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to load user stats';
            set(
              {
                isLoading: false,
                error: errorMessage,
              },
              false,
              'userStats/loadStats/error'
            );
            throw error;
          }
        },

        loadInteractions: async (
          limit = 5,
          options: { journeyOnly?: boolean } = {}
        ) => {
          try {
            set(
              { isLoading: true, error: null },
              false,
              'userStats/loadInteractions/start'
            );

            const apiService = getApiService();
            const interactions = await apiService.getInteractions(
              limit,
              options
            );

            set(
              {
                interactions,
                isLoading: false,
                error: null,
              },
              false,
              'userStats/loadInteractions/success'
            );
          } catch (error) {
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to load interactions';
            set(
              {
                isLoading: false,
                error: errorMessage,
              },
              false,
              'userStats/loadInteractions/error'
            );
            throw error;
          }
        },

        loadProgress: async () => {
          try {
            set(
              { isLoading: true, error: null },
              false,
              'userStats/loadProgress/start'
            );

            const apiService = getApiService();
            const progress = await apiService.getProgress();

            set(
              {
                progress,
                isLoading: false,
                error: null,
              },
              false,
              'userStats/loadProgress/success'
            );
          } catch (error) {
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to load progress';
            set(
              {
                isLoading: false,
                error: errorMessage,
              },
              false,
              'userStats/loadProgress/error'
            );
            throw error;
          }
        },

        setLoading: (loading: boolean) => {
          set({ isLoading: loading }, false, 'userStats/setLoading');
        },

        setError: (error: string | null) => {
          set({ error }, false, 'userStats/setError');
        },
      },
    }),
    {
      name: 'user-stats-store',
    }
  )
);
